/* eslint-disable jsx-a11y/interactive-supports-focus */
import React from 'react';
import { Control } from 'react-hook-form';
import classNames from 'classnames';

import { Section } from './data';
import { useDynamicFormSections } from './DynamicFormSections';
import DynamicQuestion from './DynamicQuestion';
import DynamicFormSectionScore from './DynamicFormSectionScore';

interface DynamicFormSectionProps {
  section: Section;
  control: Control;
}

export default function DynamicFormSection({ section, control }: DynamicFormSectionProps) {
  const [id, setId] = React.useState<string | undefined>();
  const [height, setHeight] = React.useState(0);
  const observer = React.useRef<ResizeObserver>(null);
  const { register, sections, toggle } = useDynamicFormSections();

  const handleToggle = () => {
    toggle(id);
  };

  // Resizer
  const handleResizer = React.useCallback((node: HTMLDivElement) => {
    if (!observer.current) {
      observer.current = new ResizeObserver(([entry]) => {
        setHeight(entry.contentRect.height);
      });

      observer.current.observe(node);
    }
  }, []);

  // Section state
  const s = React.useMemo(() => {
    if (id) return sections[id];
    return undefined;
  }, [id, sections]);

  // Convert height to pixels
  const heightPx = React.useMemo(() => {
    if (!(s && s.visible)) return '0px';
    if (Number.isNaN(height)) return '0px';
    return `${(height + 16)}px`;
  }, [height, s]);

  // Register the section
  React.useEffect(() => {
    if (!id) setId(register());
  }, [id, register]);

  // Disconnect observer
  React.useEffect(() => (() => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
  }), []);

  if (!s) return null;

  return (
    <div className="dynamic-form-section">
      <div className={classNames('dynamic-form-section-header', { active: s.visible })} onClick={handleToggle} role="button" onKeyUp={handleToggle}>
        <div>{section.label}</div>
        <div className="text-muted">
          <DynamicFormSectionScore section={section} control={control} />
        </div>
      </div>
      <div className={classNames('dynamic-form-section-content', { active: s.visible })} style={{ height: heightPx }}>
        <div className="dynamic-form-section-content-padding" ref={handleResizer}>
          {
            section.questions.map((question) => (
              <DynamicQuestion
                key={question.label}
                section={section}
                question={question}
                control={control}
              />
            ))
          }
        </div>
      </div>
    </div>
  );
}
